import { FC, PropsWithChildren, useMemo } from 'react';
import { css } from '@emotion/react';
import { TaskStatus } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/enums.pb';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Accordion, NotificationBadge } from '@frontend/design-system';
import { CallTasks, CallTasksProps } from '../call-tasks';

interface TaskAccordionNamespace extends FC<PropsWithChildren> {
  Header: typeof TaskAccordionHeader;
  Body: typeof TaskAccordionBody;
}

export const TaskAccordion: TaskAccordionNamespace = ({ children }) => {
  return (
    <Accordion>
      <Accordion.Item value='taskAccordion'>{children}</Accordion.Item>
    </Accordion>
  );
};

const TaskAccordionHeader: FC<{ title?: string; tasks: CallIntelTypes.Task[] }> = ({ title, tasks }) => {
  const { t } = useTranslation('call-intel-core');

  const incompleteTasks = useMemo(
    () =>
      tasks.filter(({ status }) => status === TaskStatus.STATUS_TODO || status === TaskStatus.STATUS_IN_PROGRESS) || [],
    [tasks]
  );
  return (
    <Accordion.Header title={title || t('Tasks')}>
      <div css={styles.headerIcon}>
        {incompleteTasks.length > 0 && (
          <NotificationBadge css={styles.notificationBadge} truncateCount>
            {incompleteTasks?.length}
          </NotificationBadge>
        )}
      </div>
    </Accordion.Header>
  );
};

type TaskAccordionBodyProps = CallTasksProps;

const TaskAccordionBody: FC<TaskAccordionBodyProps> = (props) => {
  return (
    <Accordion.Body css={styles.accordionBody}>
      <CallTasks {...props} />
    </Accordion.Body>
  );
};

const styles = {
  headerIcon: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    margin-left: ${theme.spacing(1)};
  `,
  notificationBadge: css`
    height: ${theme.spacing(2)};
    width: ${theme.spacing(2)};
    padding: ${theme.spacing(0, 0.5)};
    border-radius: ${theme.borderRadius.full};
  `,
  accordionBody: css`
    padding: ${theme.spacing(0, 0)};
  `,
};

TaskAccordion.Header = TaskAccordionHeader;
TaskAccordion.Body = TaskAccordionBody;
